import { ImpactFundPricingModel, ImpactType, PublicationStatus } from '@prisma/client';
import { z } from 'zod';
import Decimal from 'decimal.js';
import { currencyCodes, type Currency } from '../locale/currencies';
import { slugRegex } from '../projects/admin-project-validation';
import { ImpactShopListingType } from './impact-shop-listing-types';

const MAX_DECIMAL_24_6 = new Decimal('999999999999999999.999999');
const MAX_SIGNED_INT = 2_147_483_647;

export const impactShopPublicationStatusSchema = z.nativeEnum(PublicationStatus);
export const impactFundPricingModelSchema = z.nativeEnum(ImpactFundPricingModel);
export const impactFundImpactTypeSchema = z.nativeEnum(ImpactType).nullable();

export const impactShopIdSchema = z.number().int().positive().max(MAX_SIGNED_INT);
export const impactShopExpectedUpdatedAtSchema = z.coerce.date();
export const impactShopSlugSchema = z.string().trim().min(1).max(191).regex(slugRegex, 'Slug must be kebab-case');

const positiveDecimalSchema = z
  .number()
  .positive()
  .finite()
  .refine(value => new Decimal(value).lessThanOrEqualTo(MAX_DECIMAL_24_6), 'Must fit within the supported price range')
  .refine(value => new Decimal(value).decimalPlaces() <= 6, 'Must have no more than 6 decimal places');
const positiveMinorUnitSchema = z.number().int().positive().max(MAX_SIGNED_INT);

export const impactShopCurrencyPricesSchema = z
  .object({
    GBP: positiveDecimalSchema,
    USD: positiveDecimalSchema,
    EUR: positiveDecimalSchema,
    CAD: positiveDecimalSchema,
    AUD: positiveDecimalSchema,
  })
  .strict();

export const projectUnitPricesSchema = impactShopCurrencyPricesSchema;

export const impactShopMinimumContributionsSchema = z
  .object({
    GBP: positiveMinorUnitSchema,
    USD: positiveMinorUnitSchema,
    EUR: positiveMinorUnitSchema,
    CAD: positiveMinorUnitSchema,
    AUD: positiveMinorUnitSchema,
  })
  .strict();

const fixedPriceQuantityCommercialsObjectSchema = z
  .object({
    pricingModel: z.literal(ImpactFundPricingModel.FIXED_UNIT_PRICE),
    minQuantity: positiveDecimalSchema,
    maxQuantity: positiveDecimalSchema,
  })
  .strict();

const validateQuantityRange = <T extends { minQuantity: number; maxQuantity: number }>(commercials: T) =>
  commercials.minQuantity <= commercials.maxQuantity;

export const fixedPriceCommercialsSchema = fixedPriceQuantityCommercialsObjectSchema
  .extend({ unitPrices: impactShopCurrencyPricesSchema })
  .refine(validateQuantityRange, {
    path: ['maxQuantity'],
    message: 'Maximum quantity must be greater than or equal to minimum quantity',
  });

export const fixedPriceQuantityCommercialsSchema = fixedPriceQuantityCommercialsObjectSchema.refine(
  validateQuantityRange,
  {
    path: ['maxQuantity'],
    message: 'Maximum quantity must be greater than or equal to minimum quantity',
  },
);

export const variableContributionCommercialsSchema = z
  .object({
    pricingModel: z.literal(ImpactFundPricingModel.VARIABLE_CONTRIBUTION),
    minimumContributions: impactShopMinimumContributionsSchema,
  })
  .strict();

export const impactShopCommercialsSchema = z.union([
  fixedPriceQuantityCommercialsSchema,
  variableContributionCommercialsSchema,
]);

export const projectListingTargetSchema = z
  .object({
    targetType: z.literal(ImpactShopListingType.PROJECT),
    projectId: impactShopIdSchema,
  })
  .strict();

export const impactFundListingTargetSchema = z
  .object({
    targetType: z.literal(ImpactShopListingType.IMPACT_FUND),
    impactFundId: impactShopIdSchema,
  })
  .strict();

export const impactShopListingTargetSchema = z.discriminatedUnion('targetType', [
  projectListingTargetSchema,
  impactFundListingTargetSchema,
]);

export const impactFundFields = {
  slug: impactShopSlugSchema,
  title: z.string().trim().min(1).max(191),
  shortDescription: z.string().trim().min(1).max(191),
  description: z.string().trim().min(1).nullable(),
  impactType: impactFundImpactTypeSchema,
  pricingModel: impactFundPricingModelSchema,
};

export const impactFundSchema = z
  .object(impactFundFields)
  .strict()
  .superRefine((fund, ctx) => {
    if (fund.pricingModel === ImpactFundPricingModel.FIXED_UNIT_PRICE && fund.impactType === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['impactType'],
        message: 'Unit-priced funds must have an impact type',
      });
    }
    if (fund.pricingModel === ImpactFundPricingModel.VARIABLE_CONTRIBUTION && fund.impactType !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['impactType'],
        message: 'Donation funds cannot have an impact type',
      });
    }
  });

export const impactFundImageFields = {
  imageKey: z.string().trim().min(1).max(191),
  alt: z.string().trim().max(191).nullable(),
};

const impactFundAllocationFields = {
  projectId: impactShopIdSchema,
  percentage: z
    .number()
    .positive()
    .max(1)
    .finite()
    .refine(value => new Decimal(value).decimalPlaces() <= 9, 'Must have no more than 9 decimal places'),
};

const manualImpactFundAllocationSchema = z
  .object({
    ...impactFundAllocationFields,
    inheritProjectPrice: z.literal(false).default(false),
    unitPrices: impactShopCurrencyPricesSchema.optional(),
  })
  .transform((allocation, ctx) => {
    if (!allocation.unitPrices) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitPrices'],
        message: 'Manual allocation prices are required when project price inheritance is disabled',
      });
      return z.NEVER;
    }
    return { ...allocation, unitPrices: allocation.unitPrices };
  });

export const impactFundAllocationSchema = z
  .union([
    z.object({
      ...impactFundAllocationFields,
      inheritProjectPrice: z.literal(true),
      unitPrices: impactShopCurrencyPricesSchema.nullable().optional(),
    }),
    manualImpactFundAllocationSchema,
  ])
  .transform(allocation => (allocation.inheritProjectPrice ? { ...allocation, unitPrices: null } : allocation));

export const impactFundAllocationsSchema = z.array(impactFundAllocationSchema).superRefine((allocations, ctx) => {
  const projectIds = allocations.map(allocation => allocation.projectId);
  if (new Set(projectIds).size !== projectIds.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Each project can only be allocated once',
    });
  }
});

export function calculateImpactFundUnitPrices(
  allocations: Array<{
    percentage: string | number;
    unitPrices: Record<Currency, string | number>;
  }>,
): z.infer<typeof impactShopCurrencyPricesSchema> {
  const totals = Object.fromEntries(currencyCodes.map(currency => [currency, new Decimal(0)])) as Record<
    Currency,
    Decimal
  >;
  allocations.forEach(allocation => {
    const percentage = new Decimal(allocation.percentage);
    currencyCodes.forEach(currency => {
      totals[currency] = totals[currency].plus(percentage.times(allocation.unitPrices[currency]));
    });
  });
  return Object.fromEntries(
    currencyCodes.map(currency => [currency, totals[currency].toDecimalPlaces(6).toNumber()]),
  ) as z.infer<typeof impactShopCurrencyPricesSchema>;
}

export type ImpactShopReadinessIssueCode =
  | 'MISSING_TARGET'
  | 'MULTIPLE_TARGETS'
  | 'TARGET_NOT_AVAILABLE'
  | 'PROJECT_IMAGE_REQUIRED'
  | 'COMMERCIALS_REQUIRED'
  | 'COMMERCIALS_INVALID'
  | 'FUND_CONTENT_REQUIRED'
  | 'FUND_SHORT_DESCRIPTION_REQUIRED'
  | 'FUND_IMPACT_TYPE_REQUIRED'
  | 'FUND_IMAGE_REQUIRED'
  | 'FUND_ALLOCATIONS_REQUIRED'
  | 'FUND_ALLOCATION_PERCENTAGE_INVALID'
  | 'FUND_ALLOCATION_TOTAL_INVALID'
  | 'FUND_ALLOCATION_PRICES_INVALID'
  | 'FUND_ALLOCATION_PROJECT_PRICES_REQUIRED'
  | 'FUND_ALLOCATION_PROJECT_NOT_LIVE'
  | 'FUND_ALLOCATION_IMPACT_TYPE_INVALID';

export type ImpactShopReadinessIssue = {
  code: ImpactShopReadinessIssueCode;
  path: string[];
  message: string;
};

export type ImpactShopReadiness = {
  isReady: boolean;
  issues: ImpactShopReadinessIssue[];
};

export type ImpactFundActivationValidationInput = {
  title: string;
  shortDescription: string;
  description: string | null;
  pricingModel: ImpactFundPricingModel;
  impactType: ImpactType | null;
  imageCount: number;
  allocations: Array<{
    percentage: string | number;
    unitPrices: {
      GBP: string | number;
      USD: string | number;
      EUR: string | number;
      CAD: string | number;
      AUD: string | number;
    } | null;
    inheritProjectPrice?: boolean;
    project: { status: PublicationStatus; impactType: ImpactType };
  }>;
};

export function getImpactFundActivationReadiness(input: ImpactFundActivationValidationInput): ImpactShopReadiness {
  const issues: ImpactShopReadinessIssue[] = [];

  if (!input.title.trim() || !input.description?.trim()) {
    issues.push({
      code: 'FUND_CONTENT_REQUIRED',
      path: ['fund'],
      message: 'An active fund requires a title and description',
    });
  }

  if (!input.shortDescription.trim()) {
    issues.push({
      code: 'FUND_SHORT_DESCRIPTION_REQUIRED',
      path: ['fund', 'shortDescription'],
      message: 'An active fund requires a short description',
    });
  }

  if (input.pricingModel === ImpactFundPricingModel.FIXED_UNIT_PRICE && input.impactType === null) {
    issues.push({
      code: 'FUND_IMPACT_TYPE_REQUIRED',
      path: ['fund', 'impactType'],
      message: 'A unit-priced fund requires an impact type',
    });
  }

  if (input.imageCount === 0) {
    issues.push({
      code: 'FUND_IMAGE_REQUIRED',
      path: ['fund', 'images'],
      message: 'An active fund requires at least one image',
    });
  }

  if (input.allocations.length === 0) {
    issues.push({
      code: 'FUND_ALLOCATIONS_REQUIRED',
      path: ['fund', 'allocations'],
      message: 'An active fund requires at least one allocation',
    });
  } else {
    if (
      input.allocations.some(allocation => {
        const percentage = new Decimal(allocation.percentage);
        return percentage.lessThanOrEqualTo(0) || percentage.greaterThan(1) || percentage.decimalPlaces() > 9;
      })
    ) {
      issues.push({
        code: 'FUND_ALLOCATION_PERCENTAGE_INVALID',
        path: ['fund', 'allocations'],
        message: 'Every allocation percentage must be greater than 0 and no more than 1',
      });
    }

    const allocationTotal = input.allocations.reduce(
      (total, allocation) => total.plus(allocation.percentage),
      new Decimal(0),
    );
    if (!allocationTotal.equals(1)) {
      issues.push({
        code: 'FUND_ALLOCATION_TOTAL_INVALID',
        path: ['fund', 'allocations'],
        message: 'Fund allocations must total 1',
      });
    }

    if (
      input.allocations.some(
        allocation =>
          (!allocation.inheritProjectPrice && allocation.unitPrices === null) ||
          (allocation.unitPrices !== null &&
            Object.values(allocation.unitPrices).some(price => {
              const decimalPrice = new Decimal(price);
              return decimalPrice.lessThanOrEqualTo(0) || decimalPrice.decimalPlaces() > 6;
            })),
      )
    ) {
      issues.push({
        code: 'FUND_ALLOCATION_PRICES_INVALID',
        path: ['fund', 'allocations'],
        message: 'Every allocation requires a positive unit price in each currency',
      });
    }

    if (input.allocations.some(allocation => allocation.inheritProjectPrice && allocation.unitPrices === null)) {
      issues.push({
        code: 'FUND_ALLOCATION_PROJECT_PRICES_REQUIRED',
        path: ['fund', 'allocations'],
        message: 'Add project prices before inheriting them in a fund allocation',
      });
    }

    if (input.allocations.some(allocation => allocation.project.status !== PublicationStatus.LIVE)) {
      issues.push({
        code: 'FUND_ALLOCATION_PROJECT_NOT_LIVE',
        path: ['fund', 'allocations'],
        message: 'Every allocated project must be live',
      });
    }

    if (
      input.impactType !== null &&
      input.allocations.some(allocation => allocation.project.impactType !== input.impactType)
    ) {
      issues.push({
        code: 'FUND_ALLOCATION_IMPACT_TYPE_INVALID',
        path: ['fund', 'allocations'],
        message: 'Every allocated project must match the fund impact type',
      });
    }
  }

  return { isReady: issues.length === 0, issues };
}
